import { Link } from "react-router-dom";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PillButton } from "./PillButton";

export const Section = ({ id, eyebrow, title, intro, children }) => (
  <section className="mt-20" id={id} data-testid={`hiw-section-${id}`}>
    <div className="font-mono-label text-[11px] font-semibold text-oath-accent">{eyebrow}</div>
    <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-oath-ink sm:text-3xl">{title}</h2>
    {intro && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-oath-secondary sm:text-base">{intro}</p>}
    <div className="mt-8">{children}</div>
  </section>
);

export const Steps = ({ steps }) => (
  <ol className="relative space-y-4 border-l border-gray-300 pl-8 sm:pl-10" data-testid="hiw-steps">
    {steps.map((s, i) => (
      <li key={s.t} className="relative rounded-2xl border border-gray-200 bg-white p-5 transition-colors hover:border-oath-accent/40 sm:p-6" data-testid={`hiw-step-${i + 1}`}>
        <span className="absolute -left-[46px] top-5 grid h-7 w-7 place-items-center rounded-full bg-oath-ink font-mono text-[11px] font-semibold text-white sm:-left-[54px]">{i + 1}</span>
        <h3 className="font-display text-lg font-bold text-oath-ink">{s.t}</h3>
        <p className="mt-2 text-sm leading-relaxed text-oath-secondary">{s.d}</p>
      </li>
    ))}
  </ol>
);

export const InfoGrid = ({ items, testid }) => (
  <div className="grid gap-4 md:grid-cols-2" data-testid={testid}>
    {items.map((it) => (
      <div key={it.t} className="rounded-2xl border border-gray-200 bg-white p-6">
        {it.icon && <it.icon className="h-5 w-5 text-oath-accent" strokeWidth={1.75} />}
        <h3 className="mt-3 font-display text-base font-bold text-oath-ink">{it.t}</h3>
        <p className="mt-2 text-sm leading-relaxed text-oath-secondary">{it.d}</p>
      </div>
    ))}
  </div>
);

export const Faq = ({ items }) => (
  <Accordion type="single" collapsible className="rounded-2xl border border-gray-200 bg-white px-5 sm:px-6" data-testid="hiw-faq">
    {items.map((f, i) => (
      <AccordionItem key={f.q} value={`q${i}`} className="border-gray-200 last:border-0">
        <AccordionTrigger className="font-display text-base font-semibold text-oath-ink hover:no-underline" data-testid={`hiw-faq-q-${i}`}>{f.q}</AccordionTrigger>
        <AccordionContent className="leading-relaxed text-oath-secondary">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const CtaBand = ({ title, text, to, label, testid, other }) => (
  <div className="mt-20 rounded-3xl bg-oath-navy p-8 text-white sm:p-12" data-testid="hiw-cta">
    <h2 className="max-w-xl font-display text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2>
    <p className="mt-3 max-w-xl text-sm text-slate-300 sm:text-base">{text}</p>
    <div className="mt-8 flex flex-wrap items-center gap-4">
      <PillButton to={to} variant="accent" size="lg" data-testid={testid}>{label}</PillButton>
      <Link to={other.to} className="text-sm text-slate-300 underline-offset-4 hover:text-white hover:underline" data-testid="hiw-other-page-link">{other.label}</Link>
    </div>
  </div>
);

export const Hero = ({ eyebrow, title, text, to, label, testid }) => (
  <header className="animate-fade-up">
    <div className="font-mono-label text-[11px] font-semibold text-oath-accent">{eyebrow}</div>
    <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold tracking-tight text-oath-ink sm:text-5xl lg:text-6xl" data-testid="hiw-title">{title}</h1>
    <p className="mt-5 max-w-2xl text-base leading-relaxed text-oath-secondary">{text}</p>
    <PillButton to={to} size="lg" className="mt-8" data-testid={testid}>{label}</PillButton>
  </header>
);
